import * as React from "react";
import { Dialog, Button, RadioGroup, Label } from "@/components/ui";
import { VideoProjectServices } from "@/services";
import { VideoProject } from "@/interfaces";
import { Copy, Loader2, Check } from "lucide-react";

interface OwnProps {
  open: boolean;
  videoProjectId: number;
  isVideoProjectArchived: boolean;
  onClose: () => void;
  onDuplicate: (videoProject: VideoProject) => void;
}
type Props = OwnProps;

const DuplicateVideoProjectModalFC: React.FC<Props> = ({
  open,
  videoProjectId,
  isVideoProjectArchived,
  onClose,
  onDuplicate,
}) => {
  const [archiveAfterDuplicating, setArchiveAfterDuplicating] = React.useState<boolean>(true);
  const [duplicating, setDuplicating] = React.useState<boolean>(false);
  const [newVideoProject, setNewVideoProject] = React.useState<VideoProject | null>(null);

  const duplicateVideoProject = (): void => {
    setDuplicating(true);
    VideoProjectServices.duplicate(videoProjectId, !isVideoProjectArchived && archiveAfterDuplicating)
      .then(setNewVideoProject)
      .catch((e: any) => alert(e))
      .finally(() => setDuplicating(false));
  };

  const getDescription = (): JSX.Element => {
    if (newVideoProject) {
      return (
        <div className="flex flex-col items-center justify-center py-4 select-none gap-2">
          <div className="bg-emerald-50 text-emerald-600 rounded-full p-2">
            <Check className="size-6 animate-bounce" />
          </div>
          <p className="text-sm font-semibold text-slate-800">Video project has been duplicated.</p>
        </div>
      );
    } else if (duplicating) {
      return (
        <div className="flex flex-col items-center justify-center py-4 select-none gap-2">
          <Loader2 className="animate-spin size-6 text-indigo-600 shrink-0" />
          <p className="text-sm font-semibold text-slate-600">Duplicating video project...</p>
        </div>
      );
    } else if (isVideoProjectArchived) {
      return (
        <p className="text-sm font-medium text-slate-600 leading-relaxed text-left">
          Are you sure you want to duplicate this video project?
        </p>
      );
    } else {
      return (
        <div className="flex flex-col gap-4 text-left select-none">
          <p className="text-sm font-medium text-slate-600 leading-relaxed">
            Would you like to archive the current project after duplicating?
          </p>
          <RadioGroup
            value={archiveAfterDuplicating ? "Yes" : "No"}
            onValueChange={val => {
              setArchiveAfterDuplicating(val === "Yes");
            }}
            className="flex flex-row items-center gap-6 pl-1"
          >
            <div className="flex items-center space-x-2">
              <RadioGroup.Item value="Yes" id="archive-yes" />
              <Label htmlFor="archive-yes" className="text-slate-800 font-semibold cursor-pointer">
                Yes
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroup.Item value="No" id="archive-no" />
              <Label htmlFor="archive-no" className="text-slate-800 font-semibold cursor-pointer">
                No
              </Label>
            </div>
          </RadioGroup>
        </div>
      );
    }
  };

  const showCloseButton = (): boolean => {
    return !duplicating && (!newVideoProject || !archiveAfterDuplicating);
  };

  const closeButton = (): JSX.Element | undefined => {
    if (!showCloseButton()) return undefined;

    let buttonText = newVideoProject ? "Close" : "Cancel";

    return (
      <Button data-elm-id="duplicateVideoProjectModalCloseBtn" variant="outline" size="sm" onClick={() => onClose()}>
        {buttonText}
      </Button>
    );
  };

  const primaryButton = (): JSX.Element => {
    if (newVideoProject) {
      return (
        <Button
          data-elm-id="duplicateVideoProjectModalGoToBtn"
          variant="default"
          size="sm"
          onClick={() => onDuplicate(newVideoProject)}
        >
          Go to new project
        </Button>
      );
    } else {
      return (
        <Button
          data-elm-id="duplicateVideoProjectModalDuplicateBtn"
          variant="default"
          size="sm"
          disabled={duplicating}
          onClick={duplicateVideoProject}
          className="flex items-center gap-1.5"
        >
          {duplicating && <Loader2 className="animate-spin size-3.5 shrink-0" />}
          <span>Duplicate</span>
        </Button>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen && showCloseButton()) onClose();
      }}
      size="sm"
      showCloseButton={showCloseButton()}
    >
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-1.5 text-slate-800 text-base font-bold">
          <Copy className="size-5 text-indigo-600" />
          <span>Duplicate Video Project</span>
        </Dialog.Title>
      </Dialog.Header>

      <Dialog.Content className="p-5 overflow-y-auto max-h-[50vh]">{getDescription()}</Dialog.Content>

      <Dialog.Footer className="flex flex-row justify-end gap-2.5 p-5 border-t border-slate-100 select-none">
        {closeButton()}
        {primaryButton()}
      </Dialog.Footer>
    </Dialog>
  );
};

export const DuplicateVideoProjectModal = DuplicateVideoProjectModalFC;
